import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Loader, Calendar as CalendarIcon, Plus, X, User, Mail, Phone, Users, FileText, Edit3, Trash2, Lock, Unlock } from 'lucide-react';
import { bookingService, apartmentService } from '../../services/api';
import toast from 'react-hot-toast';

interface Booking {
  id: number;
  code_reservation: string;
  date_debut: string;
  date_fin: string;
  status: string;
  montant_total: number;
  nb_personnes?: number;
  notes?: string;
  apartment?: { id: number; titre: string };
  locataire?: { prenom: string; nom: string; email?: string; telephone?: string };
  guest?: { name: string; email?: string; phone?: string };
}

interface CalendarBlock {
  id: number;
  apartment_id: number;
  date_debut: string;
  date_fin: string;
  type_blocage: string;
  motif?: string;
  apartment?: { id: number; titre: string };
}

interface Apartment {
  id: number;
  titre: string;
  couleur_calendrier?: string;
}

const Calendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [blocks, setBlocks] = useState<CalendarBlock[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedApartment, setSelectedApartment] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  // Modal de création/modification de réservation
  const [showModal, setShowModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newBooking, setNewBooking] = useState({
    apartment_id: 0,
    date_debut: '',
    date_fin: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    nb_personnes: 1,
    notes: '',
    status: 'confirmed' as 'confirmed' | 'pending' | 'completed'
  });

  // Modal de blocage
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState<CalendarBlock | null>(null);
  const [newBlock, setNewBlock] = useState({
    apartment_id: 0,
    date_debut: '',
    date_fin: '',
    type_blocage: 'maintenance',
    motif: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, apartmentsRes, calendarRes] = await Promise.all([
        bookingService.getGerantBookings(),
        apartmentService.getMyApartments(),
        bookingService.getCalendarEvents()
      ]);
      setBookings(bookingsRes.data.bookings || []);
      setApartments(apartmentsRes.data.apartments || []);
      
      // Extraire les blocages des événements calendrier
      const events = calendarRes.data.events || [];
      const blockEvents = events.filter((e: any) => e.type === 'block').map((e: any) => ({
        id: e.id,
        apartment_id: e.apartment_id,
        date_debut: e.start,
        date_fin: e.end,
        type_blocage: e.block_type || 'autre',
        motif: e.motif,
        apartment: { id: e.apartment_id, titre: e.title?.replace('🔒 ', '') || 'Appartement' }
      }));
      setBlocks(blockEvents);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Lundi = 0
    
    const days: (number | null)[] = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day: number) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return bookings.filter(b => {
      if (selectedApartment && b.apartment?.id !== selectedApartment) return false;
      if (b.status === 'cancelled') return false;
      const start = new Date(b.date_debut);
      const end = new Date(b.date_fin);
      const current = new Date(dateStr);
      // Le jour de fin (checkout) n'est pas colorié car disponible pour nouvelle réservation
      return current >= start && current < end;
    });
  };

  const getClientName = (booking: Booking) => {
    if (booking.locataire) return `${booking.locataire.prenom} ${booking.locataire.nom}`;
    if (booking.guest) return booking.guest.name;
    return 'Client';
  };

  // Ordre fixe des appartements
  const apartmentOrder = ['adja', 'nabou', 'fatou', 'nogaye'];

  // Couleurs par appartement
  const apartmentColors: { [key: string]: { confirmed: string; pending: string; completed: string; border: string; label: string } } = {
    'nabou': {
      confirmed: 'bg-green-700',
      pending: 'bg-green-300',
      completed: 'bg-neutral-400',
      border: 'border-l-green-600',
      label: 'Appart Nabou'
    },
    'adja': {
      confirmed: 'bg-blue-700',
      pending: 'bg-blue-300',
      completed: 'bg-neutral-400',
      border: 'border-l-blue-600',
      label: 'Appart Adja'
    },
    'fatou': {
      confirmed: 'bg-orange-600',
      pending: 'bg-orange-300',
      completed: 'bg-neutral-400',
      border: 'border-l-orange-500',
      label: 'Appart Fatou'
    },
    'nogaye': {
      confirmed: 'bg-yellow-600',
      pending: 'bg-yellow-300',
      completed: 'bg-neutral-400',
      border: 'border-l-yellow-500',
      label: 'Appart Nogaye'
    },
    'default': {
      confirmed: 'bg-teal-700',
      pending: 'bg-teal-300',
      completed: 'bg-neutral-400',
      border: 'border-l-teal-500',
      label: 'Autre'
    }
  };

  const getApartmentKey = (apartmentName: string | undefined): string => {
    if (!apartmentName) return 'default';
    const name = apartmentName.toLowerCase();
    if (name.includes('nabou')) return 'nabou';
    if (name.includes('adja')) return 'adja';
    if (name.includes('fatou')) return 'fatou';
    if (name.includes('nogaye')) return 'nogaye';
    return 'default';
  };

  // Obtenir la réservation pour un appartement et un jour donné
  const getBookingForApartmentAndDay = (aptKey: string, day: number): Booking | null => {
    if (!day) return null;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return bookings.find(b => {
      if (b.status === 'cancelled') return false;
      const bookingAptKey = getApartmentKey(b.apartment?.titre);
      if (bookingAptKey !== aptKey) return false;
      const start = new Date(b.date_debut);
      const end = new Date(b.date_fin);
      const current = new Date(dateStr);
      // Le jour de fin (checkout) n'est pas colorié car disponible pour nouvelle réservation
      return current >= start && current < end;
    }) || null;
  };

  const getBookingColor = (status: string, apartmentName: string | undefined) => {
    // Réservation terminée = gris
    if (status === 'completed') return 'bg-neutral-400';
    
    const aptKey = getApartmentKey(apartmentName);
    const colors = apartmentColors[aptKey] || apartmentColors['default'];
    
    switch (status) {
      case 'confirmed': return colors.confirmed;
      case 'pending': return colors.pending;
      default: return 'bg-neutral-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500';
      case 'pending': return 'bg-orange-500';
      case 'completed': return 'bg-neutral-400';
      default: return 'bg-neutral-400';
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Ouvrir le modal pour nouvelle réservation
  const openNewBookingModal = (aptKey: string, day: number) => {
    const apt = apartments.find(a => getApartmentKey(a.titre) === aptKey);
    if (!apt) {
      toast.error('Appartement non trouvé');
      return;
    }
    
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const nextDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), day + 1);
    const endDateStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
    
    setEditingBooking(null);
    setNewBooking({
      apartment_id: apt.id,
      date_debut: dateStr,
      date_fin: endDateStr,
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      nb_personnes: 1,
      notes: '',
      status: 'confirmed'
    });
    setShowModal(true);
  };

  // Ouvrir le modal pour modifier une réservation
  const openEditBookingModal = (booking: Booking) => {
    setEditingBooking(booking);
    
    // Récupérer les infos du client
    let guestName = '';
    let guestEmail = '';
    let guestPhone = '';
    
    if (booking.locataire) {
      guestName = `${booking.locataire.prenom} ${booking.locataire.nom}`;
      guestEmail = booking.locataire.email || '';
      guestPhone = booking.locataire.telephone || '';
    } else if (booking.guest) {
      guestName = booking.guest.name || '';
      guestEmail = booking.guest.email || '';
      guestPhone = booking.guest.phone || '';
    }
    
    setNewBooking({
      apartment_id: booking.apartment?.id || 0,
      date_debut: booking.date_debut.split('T')[0],
      date_fin: booking.date_fin.split('T')[0],
      guest_name: guestName,
      guest_email: guestEmail,
      guest_phone: guestPhone,
      nb_personnes: booking.nb_personnes || 1,
      notes: booking.notes || '',
      status: booking.status as 'confirmed' | 'pending' | 'completed'
    });
    setShowModal(true);
  };

  // Soumettre la réservation (création ou modification)
  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newBooking.guest_name || !newBooking.guest_phone) {
      toast.error('Veuillez remplir le nom et téléphone du client');
      return;
    }
    
    if (new Date(newBooking.date_fin) <= new Date(newBooking.date_debut)) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingBooking) {
        // Modification
        await bookingService.updateGerant(editingBooking.id, {
          apartment_id: newBooking.apartment_id,
          date_debut: newBooking.date_debut,
          date_fin: newBooking.date_fin,
          guest_name: newBooking.guest_name,
          guest_email: newBooking.guest_email || undefined,
          guest_phone: newBooking.guest_phone,
          nb_personnes: newBooking.nb_personnes,
          notes: newBooking.notes || undefined,
          status: newBooking.status
        });
        toast.success('Réservation modifiée avec succès !');
      } else {
        // Création
        await bookingService.createGerant({
          apartment_id: newBooking.apartment_id,
          date_debut: newBooking.date_debut,
          date_fin: newBooking.date_fin,
          guest_name: newBooking.guest_name,
          guest_email: newBooking.guest_email || undefined,
          guest_phone: newBooking.guest_phone,
          nb_personnes: newBooking.nb_personnes,
          notes: newBooking.notes || undefined,
          status: newBooking.status
        });
        toast.success('Réservation créée avec succès !');
      }
      
      setShowModal(false);
      setEditingBooking(null);
      fetchData(); // Recharger les données
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'opération');
    } finally {
      setSubmitting(false);
    }
  };

  // Annuler une réservation
  const handleCancelBooking = async () => {
    if (!editingBooking) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) {
      return;
    }
    
    setSubmitting(true);
    try {
      await bookingService.cancel(editingBooking.id);
      toast.success('Réservation annulée');
      setShowModal(false);
      setEditingBooking(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'annulation');
    } finally {
      setSubmitting(false);
    }
  };

  // Ouvrir le modal de blocage
  const openBlockModal = (aptKey?: string, day?: number) => {
    const today = new Date();
    // Utiliser la date locale (pas UTC) pour éviter les décalages de fuseau horaire
    let dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    let endDateStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    
    if (day) {
      dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const nextDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), day + 1);
      endDateStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
    }
    
    let aptId = apartments[0]?.id || 0;
    if (aptKey) {
      const apt = apartments.find(a => getApartmentKey(a.titre) === aptKey);
      if (apt) aptId = apt.id;
    }
    
    setEditingBlock(null);
    setNewBlock({
      apartment_id: aptId,
      date_debut: dateStr,
      date_fin: endDateStr,
      type_blocage: 'maintenance',
      motif: ''
    });
    setShowBlockModal(true);
  };

  // Ouvrir le modal pour modifier un blocage existant
  const openEditBlockModal = (block: CalendarBlock) => {
    setEditingBlock(block);
    setNewBlock({
      apartment_id: block.apartment_id,
      date_debut: block.date_debut.split('T')[0],
      date_fin: block.date_fin.split('T')[0],
      type_blocage: block.type_blocage,
      motif: block.motif || ''
    });
    setShowBlockModal(true);
  };

  // Créer un blocage
  const handleSubmitBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (new Date(newBlock.date_fin) <= new Date(newBlock.date_debut)) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    
    setSubmitting(true);
    try {
      await bookingService.createGerant({
        apartment_id: newBlock.apartment_id,
        date_debut: newBlock.date_debut,
        date_fin: newBlock.date_fin,
        type: 'blocage',
        type_blocage: newBlock.type_blocage,
        motif: newBlock.motif || undefined
      });
      toast.success('Période bloquée avec succès !');
      setShowBlockModal(false);
      setEditingBlock(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors du blocage');
    } finally {
      setSubmitting(false);
    }
  };

  // Supprimer un blocage
  const handleDeleteBlock = async () => {
    if (!editingBlock) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce blocage ?')) {
      return;
    }
    
    setSubmitting(true);
    try {
      await bookingService.deleteBlock(editingBlock.id);
      toast.success('Blocage supprimé');
      setShowBlockModal(false);
      setEditingBlock(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSubmitting(false);
    }
  };

  // Vérifier si un jour est bloqué pour un appartement
  const getBlockForApartmentAndDay = (aptKey: string, day: number): CalendarBlock | null => {
    if (!day) return null;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return blocks.find(b => {
      const blockAptKey = getApartmentKey(b.apartment?.titre);
      if (blockAptKey !== aptKey) return false;
      const start = new Date(b.date_debut);
      const end = new Date(b.date_fin);
      const current = new Date(dateStr);
      return current >= start && current < end;
    }) || null;
  };

  const getApartmentById = (id: number) => apartments.find(a => a.id === id);

  const monthName = currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth(currentDate);
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const today = new Date();
  const isToday = (day: number) => 
    day === today.getDate() && 
    currentDate.getMonth() === today.getMonth() && 
    currentDate.getFullYear() === today.getFullYear();

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Calendrier</h1>
          <p className="text-neutral-600">Visualisez vos réservations</p>
        </div>
        
        <div className="flex items-center gap-2">
          <select
            value={selectedApartment || ''}
            onChange={(e) => setSelectedApartment(e.target.value ? Number(e.target.value) : null)}
            className="input w-auto"
          >
            <option value="">Tous les appartements</option>
            {apartments.map(apt => (
              <option key={apt.id} value={apt.id}>{apt.titre}</option>
            ))}
          </select>
          <button
            onClick={() => openBlockModal()}
            className="btn btn-outline border-red-300 text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <Lock className="w-4 h-4" />
            Bloquer période
          </button>
          <button
            onClick={() => {
              const today = new Date();
              // Utiliser la date locale (pas UTC) pour éviter les décalages de fuseau horaire
              const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
              const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
              setNewBooking({
                apartment_id: apartments[0]?.id || 0,
                date_debut: dateStr,
                date_fin: tomorrowStr,
                guest_name: '',
                guest_email: '',
                guest_phone: '',
                nb_personnes: 1,
                notes: '',
                status: 'confirmed'
              });
              setShowModal(true);
            }}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouvelle réservation
          </button>
        </div>
      </div>

      {/* Légende par appartement */}
      <div className="card p-4">
        <h3 className="font-semibold mb-3">Légende des couleurs</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Nabou */}
          <div className="space-y-1">
            <p className="font-medium text-sm">Appart Nabou</p>
            <div className="flex gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-700"></div>
                <span className="text-xs">Confirmée</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-300"></div>
                <span className="text-xs">En attente</span>
              </div>
            </div>
          </div>
          {/* Adja */}
          <div className="space-y-1">
            <p className="font-medium text-sm">Appart Adja</p>
            <div className="flex gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-blue-700"></div>
                <span className="text-xs">Confirmée</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-blue-300"></div>
                <span className="text-xs">En attente</span>
              </div>
            </div>
          </div>
          {/* Fatou */}
          <div className="space-y-1">
            <p className="font-medium text-sm">Appart Fatou</p>
            <div className="flex gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-orange-600"></div>
                <span className="text-xs">Confirmée</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-orange-300"></div>
                <span className="text-xs">En attente</span>
              </div>
            </div>
          </div>
          {/* Nogaye */}
          <div className="space-y-1">
            <p className="font-medium text-sm">Appart Nogaye</p>
            <div className="flex gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-yellow-600"></div>
                <span className="text-xs">Confirmée</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-yellow-300"></div>
                <span className="text-xs">En attente</span>
              </div>
            </div>
          </div>
          {/* Terminée */}
          <div className="space-y-1">
            <p className="font-medium text-sm">Terminée</p>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-neutral-400"></div>
              <span className="text-xs">Toutes</span>
            </div>
          </div>
          {/* Bloqué */}
          <div className="space-y-1">
            <p className="font-medium text-sm text-red-700">🔒 Bloqué</p>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-300"></div>
              <span className="text-xs">Indisponible</span>
            </div>
          </div>
        </div>
      </div>

      {/* Calendrier */}
      <div className="card overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-neutral-50">
          <button onClick={prevMonth} className="p-2 hover:bg-neutral-200 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold capitalize">{monthName}</h2>
            <button onClick={goToToday} className="btn btn-sm btn-outline">
              Aujourd'hui
            </button>
          </div>
          <button onClick={nextMonth} className="p-2 hover:bg-neutral-200 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Jours de la semaine */}
        <div className="grid grid-cols-7 border-b">
          {weekDays.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-neutral-500 bg-neutral-50">
              {day}
            </div>
          ))}
        </div>

        {/* Grille des jours */}
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            return (
              <div
                key={index}
                className={`min-h-[120px] border-b border-r p-1 ${
                  !day ? 'bg-neutral-50' : ''
                } ${day && isToday(day) ? 'bg-primary-50' : ''}`}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${
                      isToday(day) ? 'text-primary-600' : 'text-neutral-700'
                    }`}>
                      {day}
                    </div>
                    {/* Lignes fixes pour chaque appartement */}
                    <div className="space-y-0.5">
                      {apartmentOrder.map(aptKey => {
                        const booking = getBookingForApartmentAndDay(aptKey, day);
                        const block = getBlockForApartmentAndDay(aptKey, day);
                        const colors = apartmentColors[aptKey];
                        
                        if (selectedApartment) {
                          // Si un appartement est sélectionné, ne montrer que celui-ci
                          const selectedAptKey = apartments.find(a => a.id === selectedApartment);
                          if (selectedAptKey && getApartmentKey(selectedAptKey.titre) !== aptKey) {
                            return null;
                          }
                        }
                        
                        // Si bloqué, afficher le blocage
                        if (block) {
                          return (
                            <div
                              key={aptKey}
                              onClick={() => openEditBlockModal(block)}
                              className="text-xs py-0.5 px-1 rounded h-5 cursor-pointer bg-red-200 text-red-800 hover:bg-red-300 flex items-center gap-1"
                              title={`Bloqué: ${block.type_blocage}${block.motif ? ' - ' + block.motif : ''}`}
                            >
                              <Lock className="w-3 h-3" />
                              <span className="truncate">Fermé</span>
                            </div>
                          );
                        }
                        
                        return (
                          <div
                            key={aptKey}
                            onClick={() => booking ? openEditBookingModal(booking) : openNewBookingModal(aptKey, day)}
                            className={`text-xs py-0.5 px-1 rounded h-5 cursor-pointer ${
                              booking 
                                ? `${getBookingColor(booking.status, booking.apartment?.titre)} text-white hover:opacity-80` 
                                : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200'
                            }`}
                            title={booking ? `Cliquez pour modifier - ${getClientName(booking)}` : `Cliquez pour réserver - ${colors.label}`}
                          >
                            {booking ? (
                              <span className="truncate block">
                                {booking.status === 'completed' ? '✓' : ''} {getClientName(booking).split(' ')[0]}
                              </span>
                            ) : (
                              <span className="truncate block opacity-50 hover:opacity-100">+ {colors.label.substring(7, 12)}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Liste des réservations du mois */}
      <div className="card p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <CalendarIcon className="w-5 h-5" />
          Réservations ce mois ({bookings.filter(b => {
            if (selectedApartment && b.apartment?.id !== selectedApartment) return false;
            if (b.status === 'cancelled') return false;
            const start = new Date(b.date_debut);
            const end = new Date(b.date_fin);
            const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            return (start <= monthEnd && end >= monthStart);
          }).length})
        </h3>
        
        <div className="space-y-3">
          {bookings
            .filter(b => {
              if (selectedApartment && b.apartment?.id !== selectedApartment) return false;
              if (b.status === 'cancelled') return false;
              const start = new Date(b.date_debut);
              const end = new Date(b.date_fin);
              const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
              const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
              return (start <= monthEnd && end >= monthStart);
            })
            .sort((a, b) => new Date(a.date_debut).getTime() - new Date(b.date_debut).getTime())
            .map(booking => (
              <div key={booking.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getBookingColor(booking.status, booking.apartment?.titre)}`}></div>
                  <div>
                    <p className="font-medium">{booking.apartment?.titre}</p>
                    <p className="text-sm text-neutral-500">
                      {getClientName(booking)} • {new Date(booking.date_debut).toLocaleDateString('fr-FR')} → {new Date(booking.date_fin).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary-600">
                    {new Intl.NumberFormat('fr-FR').format(booking.montant_total)} F
                  </p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    booking.status === 'completed' ? 'bg-neutral-200 text-neutral-600' :
                    'bg-neutral-100 text-neutral-700'
                  }`}>
                    {booking.status === 'confirmed' ? 'Confirmée' :
                     booking.status === 'pending' ? 'En attente' : booking.status}
                  </span>
                </div>
              </div>
            ))}
          
          {bookings.filter(b => {
            if (selectedApartment && b.apartment?.id !== selectedApartment) return false;
            if (b.status === 'cancelled') return false;
            const start = new Date(b.date_debut);
            const end = new Date(b.date_fin);
            const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            return (start <= monthEnd && end >= monthStart);
          }).length === 0 && (
            <p className="text-neutral-500 text-center py-4">Aucune réservation ce mois</p>
          )}
        </div>
      </div>

      {/* Modal de création de réservation */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">
                {editingBooking ? 'Modifier la réservation' : 'Nouvelle réservation'}
              </h2>
              <button
                onClick={() => { setShowModal(false); setEditingBooking(null); }}
                className="p-2 hover:bg-neutral-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Code réservation si modification */}
            {editingBooking && (
              <div className="px-4 pt-3 pb-0">
                <span className="text-sm text-neutral-500">Code: </span>
                <span className="font-mono font-medium text-primary-600">{editingBooking.code_reservation}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmitBooking} className="p-4 space-y-4">
              {/* Appartement */}
              <div>
                <label className="block text-sm font-medium mb-1">Appartement</label>
                <select
                  value={newBooking.apartment_id}
                  onChange={(e) => setNewBooking({...newBooking, apartment_id: Number(e.target.value)})}
                  className="input w-full"
                  required
                >
                  <option value="">Sélectionner un appartement</option>
                  {apartments.map(apt => (
                    <option key={apt.id} value={apt.id}>{apt.titre}</option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date d'arrivée</label>
                  <input
                    type="date"
                    value={newBooking.date_debut}
                    onChange={(e) => setNewBooking({...newBooking, date_debut: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date de départ</label>
                  <input
                    type="date"
                    value={newBooking.date_fin}
                    onChange={(e) => setNewBooking({...newBooking, date_fin: e.target.value})}
                    className="input w-full"
                    required
                  />
                </div>
              </div>

              {/* Infos client */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Informations du client
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom complet *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <input
                        type="text"
                        value={newBooking.guest_name}
                        onChange={(e) => setNewBooking({...newBooking, guest_name: e.target.value})}
                        className="input w-full pl-10"
                        placeholder="Nom du client"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Téléphone *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <input
                        type="tel"
                        value={newBooking.guest_phone}
                        onChange={(e) => setNewBooking({...newBooking, guest_phone: e.target.value})}
                        className="input w-full pl-10"
                        placeholder="+221 77 123 45 67"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Email (optionnel)</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                      <input
                        type="email"
                        value={newBooking.guest_email}
                        onChange={(e) => setNewBooking({...newBooking, guest_email: e.target.value})}
                        className="input w-full pl-10"
                        placeholder="email@exemple.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Nombre de personnes et statut */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre de personnes</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={newBooking.nb_personnes}
                      onChange={(e) => setNewBooking({...newBooking, nb_personnes: Number(e.target.value)})}
                      className="input w-full pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Statut</label>
                  <select
                    value={newBooking.status}
                    onChange={(e) => setNewBooking({...newBooking, status: e.target.value as 'confirmed' | 'pending' | 'completed'})}
                    className="input w-full"
                  >
                    <option value="confirmed">Confirmée (payée)</option>
                    <option value="pending">En attente de paiement</option>
                    <option value="completed">Terminée</option>
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Notes (optionnel)</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-neutral-400" />
                  <textarea
                    value={newBooking.notes}
                    onChange={(e) => setNewBooking({...newBooking, notes: e.target.value})}
                    className="input w-full pl-10 min-h-[80px]"
                    placeholder="Notes sur la réservation..."
                  />
                </div>
              </div>

              {/* Aperçu appartement sélectionné */}
              {newBooking.apartment_id > 0 && (
                <div className={`p-3 rounded-lg border-l-4 ${apartmentColors[getApartmentKey(getApartmentById(newBooking.apartment_id)?.titre)]?.border || 'border-l-neutral-400'} bg-neutral-50`}>
                  <p className="font-medium">{getApartmentById(newBooking.apartment_id)?.titre}</p>
                  <p className="text-sm text-neutral-600">
                    {newBooking.date_debut && newBooking.date_fin && (
                      <>
                        {new Date(newBooking.date_debut).toLocaleDateString('fr-FR')} → {new Date(newBooking.date_fin).toLocaleDateString('fr-FR')}
                        {' • '}
                        {Math.ceil((new Date(newBooking.date_fin).getTime() - new Date(newBooking.date_debut).getTime()) / (1000 * 60 * 60 * 24))} nuit(s)
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Boutons */}
              <div className="flex gap-3 pt-4 border-t">
                {editingBooking && editingBooking.status !== 'cancelled' && (
                  <button
                    type="button"
                    onClick={handleCancelBooking}
                    className="btn bg-red-100 text-red-700 hover:bg-red-200 flex items-center justify-center gap-2"
                    disabled={submitting}
                  >
                    <Trash2 className="w-4 h-4" />
                    Annuler
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingBooking(null); }}
                  className="btn btn-outline flex-1"
                  disabled={submitting}
                >
                  Fermer
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      {editingBooking ? 'Modification...' : 'Création...'}
                    </>
                  ) : (
                    <>
                      {editingBooking ? <Edit3 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      {editingBooking ? 'Enregistrer' : 'Créer'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de blocage */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b bg-red-50">
              <h2 className="text-xl font-semibold flex items-center gap-2 text-red-800">
                <Lock className="w-5 h-5" />
                {editingBlock ? 'Modifier le blocage' : 'Bloquer une période'}
              </h2>
              <button
                onClick={() => { setShowBlockModal(false); setEditingBlock(null); }}
                className="p-2 hover:bg-red-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitBlock} className="p-4 space-y-4">
              {/* Appartement */}
              <div>
                <label className="block text-sm font-medium mb-1">Appartement</label>
                <select
                  value={newBlock.apartment_id}
                  onChange={(e) => setNewBlock({...newBlock, apartment_id: Number(e.target.value)})}
                  className="input w-full"
                  required
                  disabled={!!editingBlock}
                >
                  <option value="">Sélectionner un appartement</option>
                  {apartments.map(apt => (
                    <option key={apt.id} value={apt.id}>{apt.titre}</option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date début</label>
                  <input
                    type="date"
                    value={newBlock.date_debut}
                    onChange={(e) => setNewBlock({...newBlock, date_debut: e.target.value})}
                    className="input w-full"
                    required
                    disabled={!!editingBlock}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date fin</label>
                  <input
                    type="date"
                    value={newBlock.date_fin}
                    onChange={(e) => setNewBlock({...newBlock, date_fin: e.target.value})}
                    className="input w-full"
                    required
                    disabled={!!editingBlock}
                  />
                </div>
              </div>

              {/* Type de blocage */}
              <div>
                <label className="block text-sm font-medium mb-1">Type de blocage</label>
                <select
                  value={newBlock.type_blocage}
                  onChange={(e) => setNewBlock({...newBlock, type_blocage: e.target.value})}
                  className="input w-full"
                  disabled={!!editingBlock}
                >
                  <option value="maintenance">🔧 Maintenance / Travaux</option>
                  <option value="personnel">🏠 Usage personnel</option>
                  <option value="nettoyage">🧹 Nettoyage</option>
                  <option value="autre">📅 Autre</option>
                </select>
              </div>

              {/* Motif */}
              <div>
                <label className="block text-sm font-medium mb-1">Motif (optionnel)</label>
                <textarea
                  value={newBlock.motif}
                  onChange={(e) => setNewBlock({...newBlock, motif: e.target.value})}
                  className="input w-full min-h-[80px]"
                  placeholder="Raison du blocage..."
                  disabled={!!editingBlock}
                />
              </div>

              {/* Aperçu */}
              {newBlock.apartment_id > 0 && newBlock.date_debut && newBlock.date_fin && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="font-medium text-red-800">{getApartmentById(newBlock.apartment_id)?.titre}</p>
                  <p className="text-sm text-red-600">
                    Fermé du {new Date(newBlock.date_debut).toLocaleDateString('fr-FR')} au {new Date(newBlock.date_fin).toLocaleDateString('fr-FR')}
                    {' • '}
                    {Math.ceil((new Date(newBlock.date_fin).getTime() - new Date(newBlock.date_debut).getTime()) / (1000 * 60 * 60 * 24))} jour(s)
                  </p>
                </div>
              )}

              {/* Boutons */}
              <div className="flex gap-3 pt-4 border-t">
                {editingBlock && (
                  <button
                    type="button"
                    onClick={handleDeleteBlock}
                    className="btn bg-red-500 text-white hover:bg-red-600 flex items-center justify-center gap-2"
                    disabled={submitting}
                  >
                    <Unlock className="w-4 h-4" />
                    Débloquer
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setShowBlockModal(false); setEditingBlock(null); }}
                  className="btn btn-outline flex-1"
                  disabled={submitting}
                >
                  Annuler
                </button>
                {!editingBlock && (
                  <button
                    type="submit"
                    className="btn bg-red-600 text-white hover:bg-red-700 flex-1 flex items-center justify-center gap-2"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Blocage...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        Bloquer
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
